import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Cpu, Zap, Activity, RefreshCw, Loader2, HardDrive, Gauge, AlertTriangle } from "lucide-react";

const Card = ({ title, icon: Icon, children }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
    <div className="mb-3 flex items-center gap-2 text-sm font-bold text-white">{Icon && <Icon size={16} className="text-[#00F0FF]" />} {title}</div>
    {children}
  </div>
);
const Row = ({ k, v }) => (
  <div className="flex items-center justify-between border-b border-white/5 py-1.5 text-xs last:border-0">
    <span className="text-[#A1A1AA]">{k}</span><span className="font-plex text-white">{v ?? "—"}</span>
  </div>
);

export default function RenderDiagnostics() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [probing, setProbing] = useState(false);

  const load = async () => {
    setBusy(true);
    try { setData(await api.adminRenderDiagnostics()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load render diagnostics"); }
    setBusy(false);
  };
  useEffect(() => { load(); }, []);
  const probe = async () => {
    setProbing(true);
    try { const r = await api.adminRenderProbe(); setData((d) => ({ ...(d || {}), system: r.system, encoders: r.encoders })); toast.success("Hardware probe complete"); }
    catch { toast.error("Probe failed"); }
    setProbing(false);
  };

  if (!data) return <div className="p-6 text-[#A1A1AA]" data-testid="render-diag-loading">{busy ? <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin text-[#00F0FF]" /> Loading render diagnostics…</span> : "No data"}</div>;
  const sys = data.system || {}, enc = data.encoders || {}, ex = data.exports || {}, stages = data.stage_breakdown || [];
  const usableHw = enc.usable_hw_encoders || [];

  return (
    <div className="space-y-5" data-testid="render-diagnostics">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-white"><Activity size={20} className="text-[#00F0FF]" /> Render Diagnostics</h2>
          <p className="mt-1 text-xs text-[#666]">Measured hardware + live encoder probe + real export telemetry. Phase A — measurement only.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={probe} disabled={probing} data-testid="render-probe-btn" className="flex items-center gap-1 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/10 disabled:opacity-50"><Zap size={13} className={probing ? "animate-pulse" : ""} /> Re-probe Hardware</button>
          <button onClick={load} disabled={busy} data-testid="render-refresh-btn" className="flex items-center gap-1 rounded-lg bg-[#00F0FF] px-3 py-1.5 text-xs font-semibold text-black hover:brightness-110 disabled:opacity-50"><RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Refresh</button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title="System" icon={Cpu}>
          <Row k="CPU" v={sys.cpu?.model} />
          <Row k="Cores (phys/logical)" v={`${sys.cpu?.physical_cores ?? "—"} / ${sys.cpu?.logical_cores ?? "—"}`} />
          <Row k="RAM" v={sys.ram ? `${sys.ram.available_gb}GB free / ${sys.ram.total_gb}GB` : "—"} />
          <Row k="GPU" v={sys.gpu?.model} />
          <Row k="OS" v={sys.os} />
          <Row k="FFmpeg" v={(sys.ffmpeg_version || "").replace("ffmpeg version ", "")} />
          <Row k="HW decode" v={data.hw_decode?.active ? `${data.hw_decode.decoder} (active)` : `software (mode: ${data.hw_decode?.mode || "auto"})`} />
        </Card>
        <Card title="Encoder Status (live-probed)" icon={Zap}>
          <div className="mb-2 rounded-lg border px-2.5 py-1.5 text-xs font-semibold" style={{ borderColor: usableHw.length ? "#22c55e55" : "#f59e0b55", color: usableHw.length ? "#22c55e" : "#f59e0b", background: usableHw.length ? "#22c55e11" : "#f59e0b11" }}>
            {usableHw.length ? `Hardware usable: ${usableHw.join(", ")}` : "No usable hardware encoder → software (libx264)"}
          </div>
          {(enc.results || []).map((r) => (
            <div key={r.encoder} className="flex items-center justify-between border-b border-white/5 py-1.5 text-xs last:border-0" data-testid={`encoder-${r.encoder}`}>
              <span className="font-plex text-white">{r.encoder}</span>
              <span className="flex items-center gap-2">
                {r.approx_fps && <span className="text-[#A1A1AA]">{r.approx_fps} fps</span>}
                <span className={`rounded px-1.5 py-0.5 font-semibold ${r.usable ? "bg-[#22c55e]/15 text-[#22c55e]" : r.available ? "bg-[#f59e0b]/15 text-[#f59e0b]" : "bg-white/5 text-[#666]"}`}>
                  {r.usable ? "usable" : r.available ? "built, unusable" : "absent"}
                </span>
              </span>
            </div>
          ))}
          <p className="mt-2 text-[10px] leading-relaxed text-[#666]">{enc.note}</p>
        </Card>
        <Card title="Export Performance" icon={Gauge}>
          <Row k="Completed / Failed" v={`${ex.completed ?? 0} / ${ex.failed ?? 0}`} />
          <Row k="Failure rate" v={ex.failure_rate != null ? `${ex.failure_rate}%` : "—"} />
          <Row k="Avg total" v={ex.avg_total_s != null ? `${ex.avg_total_s}s` : "—"} />
          <Row k="P50 / P95 total" v={`${ex.p50_total_s ?? "—"}s / ${ex.p95_total_s ?? "—"}s`} />
          <Row k="Median render FPS" v={ex.median_render_fps} />
          <Row k="HW / SW render" v={`${ex.hw_render_pct ?? 0}% / ${ex.sw_render_pct ?? 0}%`} />
          <Row k="Base-video cache" v={data.base_video_cache ? `${data.base_video_cache.cached_files} files · ${(data.base_video_cache.cache_bytes / 1048576).toFixed(1)}MB` : "—"} />
        </Card>
      </div>

      {data.background_rendering && (
        <Card title="Continuous Background Rendering" icon={Zap}>
          <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2" data-testid="bg-render-diag">
            <div>
              <Row k="Background / Foreground renders" v={`${data.background_rendering.background_renders ?? 0} / ${data.background_rendering.foreground_renders ?? 0}`} />
              <Row k="Incremental renders" v={data.background_rendering.incremental_renders ?? 0} />
              <Row k="Base-video reuse count" v={data.background_rendering.base_reused_count ?? 0} />
            </div>
            <div>
              <Row k="Avg segment cache reuse" v={data.background_rendering.avg_segment_reuse_pct != null ? `${data.background_rendering.avg_segment_reuse_pct}%` : "—"} />
              <Row k="Avg background render" v={data.background_rendering.avg_background_total_s != null ? `${data.background_rendering.avg_background_total_s}s` : "—"} />
              <Row k="Avg foreground render" v={data.background_rendering.avg_foreground_total_s != null ? `${data.background_rendering.avg_foreground_total_s}s` : "—"} />
            </div>
          </div>
          <p className="mt-2 text-[10px] leading-relaxed text-[#666]">Editing triggers a debounced background render; only dirtied segments re-encode (the rest reuse the content-addressed segment cache), so the Export click becomes a fast finalize.</p>
        </Card>
      )}

      <Card title="Stage Breakdown (avg across completed exports)" icon={HardDrive}>
        {stages.length === 0 ? (
          <p className="text-xs text-[#666]">No completed exports recorded yet — run an export to populate stage timings.</p>
        ) : stages.map((s) => (
          <div key={s.key} className="py-1.5" data-testid={`stage-${s.key}`}>
            <div className="mb-1 flex items-center justify-between text-xs"><span className="text-white">{s.stage}</span><span className="font-plex text-[#A1A1AA]">{s.avg_s}s · {s.pct}%</span></div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-white/5"><div className="h-full rounded-full bg-gradient-to-r from-[#00F0FF] to-[#8B5CF6]" style={{ width: `${Math.min(100, s.pct)}%` }} /></div>
          </div>
        ))}
      </Card>

      <Card title="Recent Exports" icon={Activity}>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead><tr className="text-[#666]">{["Export", "Encoder", "Path", "Res", "Dur", "Total", "FPS", "Bottleneck", "Status"].map((h) => <th key={h} className="py-1.5 pr-3 font-semibold">{h}</th>)}</tr></thead>
            <tbody>
              {(data.recent || []).map((r) => (
                <tr key={r.export_id} className="border-t border-white/5" data-testid={`recent-export-${r.export_id}`}>
                  <td className="py-1.5 pr-3 font-plex text-[#A1A1AA]">{(r.export_id || "").slice(0, 8)}</td>
                  <td className="py-1.5 pr-3 text-white">{r.encoder || "—"}</td>
                  <td className="py-1.5 pr-3"><span className={r.hw_path ? "text-[#22c55e]" : "text-[#A1A1AA]"}>{r.hw_path ? "HW" : "SW"}</span></td>
                  <td className="py-1.5 pr-3 font-plex text-[#A1A1AA]">{r.width}×{r.height}</td>
                  <td className="py-1.5 pr-3 font-plex text-[#A1A1AA]">{r.duration ? `${Math.round(r.duration)}s` : "—"}</td>
                  <td className="py-1.5 pr-3 font-plex text-white">{r.total_s != null ? `${r.total_s}s` : "—"}</td>
                  <td className="py-1.5 pr-3 font-plex text-[#A1A1AA]">{r.render_fps ?? "—"}</td>
                  <td className="py-1.5 pr-3 text-[#00F0FF]">{r.bottleneck || "—"}</td>
                  <td className="py-1.5 pr-3">{r.status === "ready" ? <span className="text-[#22c55e]">ready</span> : <span className="flex items-center gap-1 text-[#ef4444]"><AlertTriangle size={11} /> {r.status}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!data.recent || data.recent.length === 0) && <p className="py-3 text-xs text-[#666]">No exports recorded yet.</p>}
        </div>
      </Card>
    </div>
  );
}
